/**
 *  @class
 *  @function ScalaTaglie
 *  Selettore taglia a tendina: il campo apre un pannello con le schede "Taglia italiana" /
 *  "Taglia sull'etichetta". La scelta seleziona il radio originale dell'opzione, così product.js
 *  aggiorna la variante come con i bottoni standard.
 *
 *  * Flusso: clic su una riga → seleziona() spunta il radio nascosto → evento "change" →
 *  * product.js (onVariantChange) ricalcola la variante → emette "product:variant-change" →
 *  * aggiornaCampo() riallinea il testo del campo.
 *  ? Il markup (campo, pannello, righe con data-italiana / data-etichetta) è generato da snippets/scala-taglie.liquid
 */
// * Guardia: il file può essere incluso più volte (uno per ogni opzione "Taglia" renderizzata)
if (!customElements.get('scala-taglie')) {
  class ScalaTaglie extends HTMLElement {
    constructor() {
      super();

      // * Riferimenti al DOM generato dallo snippet
      this.fieldset = this.closest('fieldset'); // fieldset che contiene anche i radio originali nascosti
      this.campo = this.querySelector('.scala-taglie__campo'); // bottone "Seleziona la tua taglia"
      this.valore = this.querySelector('.scala-taglie__valore'); // testo mostrato nel campo
      this.pannello = this.querySelector('.scala-taglie__pannello');
      this.schede = Array.from(this.querySelectorAll('.scala-taglie__scheda'));
      this.righe = Array.from(this.querySelectorAll('.scala-taglie__riga'));
      // * Sistema di misura attivo: 'italiana' o 'etichetta' (corrisponde a data-italiana / data-etichetta delle righe)
      this.sistema = 'italiana';
      // * true se l'utente ha già scelto una taglia (o è arrivata da ?variant= nell'URL)
      this.scelta = this.dataset.scelta === 'true';

      this.campo.addEventListener('click', () => this.toggle());
      this.schede.forEach((scheda) => {
        scheda.addEventListener('click', () => this.setSistema(scheda.dataset.sistema));
      });
      this.righe.forEach((riga) => {
        riga.addEventListener('click', () => this.seleziona(riga));
        // Accessibilità: le righe sono <li> con tabindex, quindi Invio/Spazio vanno gestiti a mano
        riga.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.seleziona(riga);
          }
        });
      });

      // Chiusura con clic esterno ed Esc
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.toggle(false);
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') {
          this.toggle(false);
          this.campo.focus();
        }
      });

      // Taglia scelta da un altro selettore (es. barra sticky): allinea il campo
      // ? Evento emesso da product.js (onVariantChange) dopo ogni cambio variante, anche quello avviato da qui
      // ! Non filtra per sectionId: con più form prodotto in pagina reagisce anche alle varianti degli altri
      document.addEventListener('product:variant-change', () => {
        this.scelta = true;
        this.aggiornaCampo();
      });

      // Aggiungi al carrello senza taglia scelta: si apre il selettore
      // * Ascolto in fase di cattura (terzo parametro true) per bloccare il submit prima degli handler del tema
      document.addEventListener('submit', (event) => {
        if (event.target.id !== this.dataset.form || this.scelta) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        // Scorrimento animato solo se l'utente non ha chiesto al sistema di ridurre le animazioni
        const ridotto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.campo.scrollIntoView({ block: 'center', behavior: ridotto ? 'auto' : 'smooth' });
        this.toggle(true);
      }, true);

      // Stato iniziale: segnaposto oppure taglia già selezionata
      this.aggiornaCampo();
    }

    // Radio originale attualmente spuntato: è la fonte di verità della taglia selezionata
    radioSelezionato() {
      return this.fieldset.querySelector('input[type="radio"]:checked');
    }

    // Apre/chiude il pannello; senza argomento inverte lo stato attuale
    toggle(apri = this.pannello.hidden) {
      this.pannello.hidden = !apri;
      this.campo.setAttribute('aria-expanded', apri);
      if (apri) {
        this.aggiornaDisponibilita();
        // Focus sulla riga selezionata (o sulla prima) per la navigazione da tastiera
        const attiva = this.righe.find((riga) => riga.getAttribute('aria-selected') === 'true') || this.righe[0];
        if (attiva) attiva.focus({ preventScroll: true });
      }
    }

    // Cambio scheda: riscrive il testo di ogni riga leggendo data-italiana o data-etichetta
    setSistema(sistema) {
      this.sistema = sistema;
      this.schede.forEach((scheda) => {
        scheda.setAttribute('aria-selected', scheda.dataset.sistema === sistema);
      });
      this.righe.forEach((riga) => {
        riga.textContent = riga.dataset[sistema];
      });
      this.aggiornaCampo();
    }

    // * Cuore dell'integrazione: non cambia la variante direttamente, spunta il radio nascosto
    // * e lancia "change" così product.js fa tutto il resto (prezzo, immagini, URL, pulsante carrello)
    seleziona(riga) {
      const radio = Array.from(this.fieldset.querySelectorAll('input[type="radio"]')).find((input) => input.value === riga.dataset.value);
      if (!radio) return;

      this.scelta = true;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.aggiornaCampo();
      this.toggle(false);
      this.campo.focus();
    }

    // Righe esaurite allineate allo stato calcolato da product.js sui radio
    // ? product.js (setDisabled) mette la classe is-disabled sui radio non disponibili per la combinazione di opzioni scelta
    // ! La classe viene solo aggiunta e mai tolta: se cambia un'altra opzione (es. colore) e la taglia torna
    // ! disponibile, la riga resta barrata. Valutare classList.toggle('is-disabled', radio.classList.contains('is-disabled'))
    aggiornaDisponibilita() {
      const radios = Array.from(this.fieldset.querySelectorAll('input[type="radio"]'));
      radios.forEach((radio) => {
        const riga = this.righe.find((r) => r.dataset.value === radio.value);
        if (riga && radio.classList.contains('is-disabled')) riga.classList.add('is-disabled');
      });
    }

    // Allinea righe e campo al radio spuntato: evidenzia la riga e mostra la taglia nel sistema attivo
    aggiornaCampo() {
      const radio = this.radioSelezionato();
      this.righe.forEach((riga) => {
        riga.setAttribute('aria-selected', this.scelta && radio && riga.dataset.value === radio.value);
      });

      // Finché l'utente non sceglie si mostra il segnaposto, anche se Shopify ha un radio già spuntato di default
      const riga = this.scelta && radio ? this.righe.find((r) => r.dataset.value === radio.value) : null;
      this.valore.textContent = riga ? riga.dataset[this.sistema] : this.valore.dataset.segnaposto;
      this.campo.classList.toggle('is-segnaposto', !riga);
    }
  }
  customElements.define('scala-taglie', ScalaTaglie);
}
